import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Search, Trash2, Mail } from "lucide-react";

function fmt(d) { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } }

export default function SubscribersAdmin() {
  const qc = useQueryClient();
  const { data: subs = [] } = useQuery({ queryKey: ["admin-subscribers"], queryFn: async () => (await api.get("/admin/newsletter")).data });
  const [q, setQ] = useState("");

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/newsletter/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["admin-subscribers"] }); },
  });

  const filtered = subs.filter((s) => (s.email || "").toLowerCase().includes(q.toLowerCase()));

  const exportCsv = async () => {
    const res = await fetch(`${API}/admin/newsletter/export`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Subscribers</h1><p className="mt-1 text-sm text-subtle">Newsletter email subscribers ({subs.length}).</p></div>
        <Button variant="outline" onClick={exportCsv} data-testid="subscribers-export"><Download className="h-4 w-4" /> Export CSV</Button>
      </div>

      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email..." className="pl-9" data-testid="subscribers-search" />
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Email</TableHead><TableHead>Subscribed</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={3} className="py-10 text-center text-subtle">No subscribers yet.</TableCell></TableRow>}
            {filtered.map((s) => (
              <TableRow key={s.id} data-testid={`subscriber-row-${s.id}`}>
                <TableCell className="flex items-center gap-2 font-medium text-ink"><Mail className="h-4 w-4 text-finblue" />{s.email}</TableCell>
                <TableCell className="text-sm text-subtle">{fmt(s.created_at)}</TableCell>
                <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={() => del.mutate(s.id)} data-testid={`subscriber-delete-${s.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
